import {
  CButton,
  CCol,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { getItem } from "../../api/generics";
import {
  ReceivedDocument,
  DOCUMENT_TYPE_LABELS,
  SALE_TYPE_CASH_LABEL,
  SALE_TYPE_CREDIT_LABEL,
  CREATION_TYPE_MANUAL,
} from "../../models/received-document";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { PYG } from "../../currency/available-currencies";
import { formatToCurrency } from "../../currency/format";
import { formatUTCDateTime } from "../../utils/dates";

interface ReceivedDocumentDetailModalProps {
  show: boolean;
  document?: ReceivedDocument;
  onCancel: () => void;
}

const DetailRow: React.FC<{ label: string; value: string | undefined }> = ({
  label,
  value,
}) => (
  <CRow className="mb-2">
    <CCol md={4}>
      <strong>{label}:</strong>
    </CCol>
    <CCol md={8}>{value ?? "—"}</CCol>
  </CRow>
);

const formatAmount = (
  amount: number | undefined,
  currency: string,
): string => {
  if (amount === undefined || amount === null) return "—";
  return formatToCurrency(amount, currency);
};

const ReceivedDocumentDetailModal: React.FC<
  ReceivedDocumentDetailModalProps
> = ({ show, document, onCancel }) => {
  const [fullDocument, setFullDocument] = useState<
    ReceivedDocument | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !document?.id) {
      setFullDocument(undefined);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      const result = await getItem<ReceivedDocument>(
        `/received_documents/${document.id}/`,
      );
      if (result.status === SUCCESS && result.data) {
        setFullDocument(result.data);
      } else {
        errorAlert("No se pudo cargar el detalle del documento.");
        onCancel();
      }
      setLoading(false);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, document?.id]);

  const title = fullDocument?.documentNumber
    ? `Documento ${fullDocument.documentNumber}`
    : "Detalle de Documento Recibido";

  const currency = fullDocument?.currency ?? PYG;

  const saleTypeLabel =
    fullDocument?.saleType === "cash"
      ? SALE_TYPE_CASH_LABEL
      : fullDocument?.saleType === "credit"
        ? SALE_TYPE_CREDIT_LABEL
        : undefined;

  const creationLabel =
    fullDocument?.creationType === CREATION_TYPE_MANUAL
      ? "Carga manual"
      : "CDC / SIFEN";

  return (
    <CModal show={show} onClose={onCancel} size="lg">
      <CModalHeader closeButton>
        <CModalTitle>{title}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          fullDocument && (
            <>
              <h6 className="text-muted mb-3">Datos del comprobante</h6>
              <DetailRow
                label="Tipo"
                value={
                  fullDocument.documentType
                    ? DOCUMENT_TYPE_LABELS[fullDocument.documentType]
                    : undefined
                }
              />
              <DetailRow
                label="N° Documento"
                value={fullDocument.documentNumber}
              />
              <DetailRow
                label="Fecha"
                value={
                  fullDocument.documentDate
                    ? formatUTCDateTime(new Date(fullDocument.documentDate))
                    : undefined
                }
              />
              <DetailRow label="Condición de venta" value={saleTypeLabel} />
              <DetailRow label="Moneda" value={fullDocument.currency} />
              {currency !== PYG && (
                <DetailRow
                  label="Tipo de cambio"
                  value={
                    fullDocument.pygExchangeRate !== undefined
                      ? String(fullDocument.pygExchangeRate)
                      : undefined
                  }
                />
              )}
              <DetailRow label="Origen" value={creationLabel} />
              {fullDocument.cdc && (
                <CRow className="mb-2">
                  <CCol md={4}>
                    <strong>CDC:</strong>
                  </CCol>
                  <CCol md={8}>
                    <code style={{ wordBreak: "break-all" }}>
                      {fullDocument.cdc}
                    </code>
                  </CCol>
                </CRow>
              )}

              <hr />
              <h6 className="text-muted mb-3">Emisor</h6>
              <DetailRow label="RUC" value={fullDocument.supplierRuc} />
              <DetailRow
                label="Razón Social"
                value={fullDocument.supplierSocialName}
              />
              {fullDocument.supplierFantasyName && (
                <DetailRow
                  label="Nombre Fantasía"
                  value={fullDocument.supplierFantasyName}
                />
              )}

              <hr />
              <h6 className="text-muted mb-3">Totales</h6>
              <DetailRow
                label="Total"
                value={formatAmount(fullDocument.amount, currency)}
              />
              <DetailRow
                label="Base gravada 10%"
                value={formatAmount(fullDocument.amount10Percent, currency)}
              />
              <DetailRow
                label="IVA 10%"
                value={formatAmount(fullDocument.iva10Percent, currency)}
              />
              <DetailRow
                label="Base gravada 5%"
                value={formatAmount(fullDocument.amount5Percent, currency)}
              />
              <DetailRow
                label="IVA 5%"
                value={formatAmount(fullDocument.iva5Percent, currency)}
              />
              <DetailRow
                label="Exento"
                value={formatAmount(fullDocument.amountExempt, currency)}
              />

              {fullDocument.obs && (
                <>
                  <hr />
                  <h6 className="text-muted mb-3">Observaciones</h6>
                  <DetailRow label="Obs." value={fullDocument.obs} />
                </>
              )}
            </>
          )
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onCancel}>
          Cerrar
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default ReceivedDocumentDetailModal;
